import React, { useState } from 'react'
import { Menu, Button } from '@amzn/alchemy-components-react'
import { useTranslation } from 'react-i18next'
import { BulkRegisterMfaModalHooks } from "src/models/bulk-register-mfa-modal-hooks";
import { AuthgardCredentialType } from "src/models/bulk-execute-authgard-admin-operation";

/**
 * Input parameters required to create Bulk Action component.
 */
interface BulkActionProps {
  /**
   * Count of the principals (users) selected.
   */
  principalArnsCount: number

  /**
   * The hooks for the Bulk Register MFA modal.
   */
  bulkRegisterMfaModalHooks: BulkRegisterMfaModalHooks

  /**
   * True when the selected site is an Authgard site, which is the only place the bulk credential
   * management entries apply. Undefined/false hides them entirely.
   */
  showAuthgardEntries?: boolean

  /**
   * True when the selection contains at least one Scan2Auth principal (an associate). Ignored unless
   * showAuthgardEntries is set.
   */
  allowScan2Auth?: boolean

  /**
   * True when the selection contains at least one FIDO2 principal (a non-associate — under Authgard
   * the credential type follows the principal's own userType). Ignored unless showAuthgardEntries is
   * set.
   */
  allowFido2?: boolean

  /**
   * Opens the bulk manage credentials wizard for the given credential type. The list owns the
   * navigation because it owns the selection being handed over.
   */
  openBulkManageCredentials?: (credentialType: AuthgardCredentialType) => void
}

/**
 * Component to create Bulk Action Dropdown.
 */
export const BulkAction = (bulkActionProps: BulkActionProps) => {
  const { t } = useTranslation()

  const [menuIcon, setMenuIcon] = useState('caretDown')

  const initializeBulkRegisterMfaModal = () => {
    bulkActionProps.bulkRegisterMfaModalHooks.setOpen(true);
  }

  const registerMfa = {
    label: t('register-mfa'),
    action: initializeBulkRegisterMfaModal
  }

  const actionMenuOptions = []
  actionMenuOptions.push(registerMfa)

  // Two separate entries rather than one combined action: each pins the credential type, so a run is
  // always a single (scope, credentialType) pair and the wizard only ever reasons about one type. An
  // entry only appears when the selection actually contains principals of that type, so the operator
  // can't open a wizard with an empty cohort.
  if (bulkActionProps.showAuthgardEntries && bulkActionProps.openBulkManageCredentials) {
    const openBulkManageCredentials = bulkActionProps.openBulkManageCredentials;

    if (bulkActionProps.allowScan2Auth) {
      actionMenuOptions.push({
        label: t('bulk-manage-scan2auth-entry'),
        action: () => openBulkManageCredentials('SCAN2AUTH')
      })
    }

    if (bulkActionProps.allowFido2) {
      actionMenuOptions.push({
        label: t('bulk-manage-fido2-entry'),
        action: () => openBulkManageCredentials('FIDO2')
      })
    }
  }

  return (
    <Menu
      className="dashboard-buttons mr-1 d-block summary-card-action"
      id="bulk-action-menu"
      options={actionMenuOptions}
      onClose={() => setMenuIcon('caretDown')}
      onOpen={() => setMenuIcon('caretUp')}
    >
      <span slot="trigger">
        <Button
            icon={menuIcon}
            iconPosition="right"
            disabled={bulkActionProps.principalArnsCount === 0}>
          {t('bulk-action', {
            "numberSelected": bulkActionProps.principalArnsCount
          })}
        </Button>
      </span>
    </Menu>
  )
}
